import { useQuery } from '@tanstack/react-query'
import { useEffect } from 'react'
import {
  IDLE_CHECK_MS,
  IDLE_STORAGE_KEY,
  IDLE_WRITE_MS,
  idleExpired,
  latestActivity,
} from '@/lib/idle-logout'
import { client, orpc, WRITE } from '@/lib/orpcClient'
import { useModulesContext } from '@/providers/modules-provider'
import { useUser } from '@/providers/user-provider'

// `localStorage` kan ontbreken of weigeren (privévenster, geblokkeerde sitedata): dan telt alleen
// dit tabblad.
function readStored(): string | null {
  try {
    return window.localStorage.getItem(IDLE_STORAGE_KEY)
  } catch {
    return null
  }
}

function writeStored(at: number) {
  try {
    window.localStorage.setItem(IDLE_STORAGE_KEY, String(at))
  } catch {
    // alleen dit tabblad dan
  }
}

/**
 * Logt uit na de minuten uit de instellingen van de urenregistratie zonder klik, toets, scroll of
 * aanraking in een van de tabbladen (besluit 23). Vlak ervoor sluit de eigen oranje registratie op
 * die laatste activiteit, niet op het moment van uitloggen. Zonder HR-module of zonder instelling
 * gebeurt er niets.
 */
export function useIdleLogout() {
  const { isModuleEnabled } = useModulesContext()
  const { current, logout } = useUser()
  const signedIn = !!current?.user
  const settingQ = useQuery({
    ...orpc.hr.timeRegistrations.idleLogout.queryOptions(),
    enabled: signedIn && isModuleEnabled('hr'),
  })
  const minutes = settingQ.data?.minutes ?? null

  useEffect(() => {
    if (!signedIn || minutes === null) return
    // Een pagina die laadt is ook activiteit.
    let last = Date.now()
    let written = last
    writeStored(last)
    let leaving = false

    const onActivity = () => {
      last = Date.now()
      if (last - written >= IDLE_WRITE_MS) {
        written = last
        writeStored(last)
      }
    }
    const check = async () => {
      if (leaving) return
      const latest = latestActivity(last, readStored())
      if (!idleExpired(latest, minutes, Date.now())) return
      leaving = true
      try {
        await client.hr.timeRegistrations.endOnline({ at: new Date(latest).toISOString() }, WRITE)
      } catch {
        // Uitloggen gaat voor: zonder dit sluit oranje twee minuten na het wegvallen van de verbinding.
      }
      await logout()
    }

    const listen = { capture: true, passive: true } as const
    const events = ['pointerdown', 'keydown', 'wheel', 'touchstart'] as const
    for (const e of events) window.addEventListener(e, onActivity, listen)
    const timer = setInterval(() => void check(), IDLE_CHECK_MS)
    return () => {
      clearInterval(timer)
      for (const e of events) window.removeEventListener(e, onActivity, listen)
    }
  }, [signedIn, minutes, logout])
}
